import {useEffect, useState} from "react";
import { Button } from "react-bootstrap";

export const filterOptions = ["House","Neighbor","Street","Driveway"];

/** CSS class for scaling cap page titles down on long names (mobile). */
export function getCapTitleLengthClass(name) {
    const len = (name || '').trim().length;
    if (len >= 50) return 'brand-cap-title--len-50';
    if (len >= 38) return 'brand-cap-title--len-38';
    if (len >= 28) return 'brand-cap-title--len-28';
    return '';
}

// Look for user data in session storage
export const getUser = () => {
    let userStr = localStorage.getItem('user');
    if (userStr) return JSON.parse(userStr);
    else {
        userStr = sessionStorage.getItem('user');
        if (userStr) return JSON.parse(userStr);
        else return null;
    }
}

// Look for token in session storage
export const getToken = () => {
    let token = localStorage.getItem('token') || null;
    if(!token) {
        token = sessionStorage.getItem('token') || null;
    }
    return token;
}

// Look for camera token in session storage
export const getCameraToken = () => {
    try {
        return JSON.parse(sessionStorage.getItem('cameraToken')) || null;
    } catch(e) {
        return sessionStorage.getItem('cameraToken') || null;
    }
}

export const setCameraToken = (t) => {
    sessionStorage.setItem('cameraToken',JSON.stringify(t));
}

export const setCameras = (cameras) => {
    sessionStorage.setItem('cameras',JSON.stringify(cameras));
}

export const getCameras = () => {
    return JSON.parse(sessionStorage.getItem('cameras')) || null;
}

export const storeCap = (cap) => {
    sessionStorage.setItem('cap',JSON.stringify(cap));
}

export const getCap = () => {
    const cap = sessionStorage.getItem('cap');
    if(!cap || cap == null || typeof cap === "undefined") {
        return null;
    } else {
        return JSON.parse(sessionStorage.getItem('cap')) || null;
    }
}

export const storeFiles = (files) => {
    sessionStorage.setItem('files',JSON.stringify(files));
}

export const getFiles = () => {
    return JSON.parse(sessionStorage.getItem('files')) || null;
}

export const storeLatestUrls = (urls) => {
    sessionStorage.setItem('urls',JSON.stringify(urls));
}

export const getLatestUrls = () => {
    return JSON.parse(sessionStorage.getItem('urls')) || null;
}

export const getDefaultEventTabs = () => {
    return JSON.parse(sessionStorage.getItem('defaultEventTabs')) || null;
}

export const setDefaultEventTabs = (tabs) => {
    sessionStorage.setItem('defaultEventTabs',JSON.stringify(tabs));
}

export const ensurePickerWallClockDate = (d) => {
    if (d == null) return new Date();
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
        return new Date(
            d.getFullYear(),
            d.getMonth(),
            d.getDate(),
            d.getHours(),
            d.getMinutes(),
            0,
            0,
        );
    }
    const s = String(d).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (m) {
        return new Date(
            Number(m[1]),
            Number(m[2]) - 1,
            Number(m[3]),
            Number(m[4]),
            Number(m[5]),
            0,
            0,
        );
    }
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return new Date();
    return new Date(
        x.getFullYear(),
        x.getMonth(),
        x.getDate(),
        x.getHours(),
        x.getMinutes(),
        0,
        0,
    );
};

/**
 * Motion timeline anchor: picker wall clock as ISO (no Z). Matches DB start_date (camera local).
 */
export const timelineAnchorIsoFromPicker = (d) => {
    const local = ensurePickerWallClockDate(d);
    if (Number.isNaN(local.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}T${pad(local.getHours())}:${pad(local.getMinutes())}:00`;
};

/** Clone motion-history params without JSON round-trip (which corrupts Date fields). */
export const cloneEventParameters = (params) => {
    if (!params) return params;
    return {
        ...params,
        selectedFilters: params.selectedFilters
            ? { ...params.selectedFilters }
            : {},
        startDate:
            params.startDate != null
                ? ensurePickerWallClockDate(params.startDate)
                : params.startDate,
        endDate:
            params.endDate != null
                ? ensurePickerWallClockDate(params.endDate)
                : params.endDate,
    };
};

export const getDefaultEventParameters = () => {
    var ep = JSON.parse(sessionStorage.getItem('defaultEventParameters')) || null;
    if (!ep) return ep;
    for (var cap in ep) {
        if (ep[cap].startDate) {
            ep[cap].startDate = ensurePickerWallClockDate(ep[cap].startDate);
        }
        if (ep[cap].endDate) {
            ep[cap].endDate = ensurePickerWallClockDate(ep[cap].endDate);
        }
    }
    return ep;
};

export const setDefaultEventParameters = (params) => {
    const clone = JSON.parse(JSON.stringify(params));
    for (const capId in clone) {
        const row = clone[capId];
        if (!row) continue;
        if (row.startDate) {
            row.startDate = timelineAnchorIsoFromPicker(
                ensurePickerWallClockDate(row.startDate),
            );
        }
        if (row.endDate) {
            row.endDate = timelineAnchorIsoFromPicker(
                ensurePickerWallClockDate(row.endDate),
            );
        }
    }
    sessionStorage.setItem("defaultEventParameters", JSON.stringify(clone));
};

export const removeDefaultEventParameters = () => {
    sessionStorage.removeItem('defaultEventParameters');
}

export const getDefaultFrameParameters = () => {
    return JSON.parse(sessionStorage.getItem('defaultFrameParameters')) || null;
}

export const setDefaultFrameParameters = (params) => {
    sessionStorage.setItem('defaultFrameParameters',JSON.stringify(params));
}

export const getDefaultPageNumber = () => {
    return JSON.parse(sessionStorage.getItem('defaultPageNumber')) || null;
}

export const setDefaultPageNumber = (pn) => {
    sessionStorage.setItem('defaultPageNumber',JSON.stringify(pn))
}

export const setDefaultCameraDirections = (dirs) => {
    sessionStorage.setItem('defaultCameraDirections',JSON.stringify(dirs));
}

export const getDefaultCameraDirections = (dirs) => {
    return JSON.parse(sessionStorage.getItem('defaultCameraDirections')) || null;
}

export const getDefaultSortParameter = () => {
    return JSON.parse(sessionStorage.getItem('defaultSortParameter')) || null;
}

export const setDefaultSortParameter = (sp) => {
    sessionStorage.setItem('defaultSortParameter',JSON.stringify(sp));
}

export const getDefaultDaylightParameter = () => {
    return JSON.parse(sessionStorage.getItem('defaultDaylightParameter')) || null;
}

export const setDefaultDaylightParameter = (dp) => {
    sessionStorage.setItem('defaultDaylightParameter',JSON.stringify(dp));
}

export const setNewCapSignup = (su) => {
    sessionStorage.setItem('newCapSignup',JSON.stringify(su));
}

export const getNewCapSignup = () => {
    return JSON.parse(sessionStorage.getItem('newCapSignup')) || false;
}

export const setFCMToken = (t) => {
    sessionStorage.setItem('fcmToken',JSON.stringify(t));
}

export const retrieveFCMToken = () => {
    return JSON.parse(sessionStorage.getItem('fcmToken'));
}

// remove the token and user from the ssion storage
export const removeUserSession = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
}

// set the token from storage
export const setUserSession = (token, user) => {
    sessionStorage.setItem('token',token);
    sessionStorage.setItem('user',JSON.stringify(user));
    localStorage.setItem('token',token);
    localStorage.setItem('user',JSON.stringify(user));
} 

export const salesClick = () => {
    window.open("https://bedrockwireless.com/",'_blank');
}

export const getNeighborhoodCollapsed = () => {
    return JSON.parse(sessionStorage.getItem('neighborhoodCollapsed')) || null;
}

export const storeNeighborhoodCollapsed = (nc) => {
    sessionStorage.setItem('neighborhoodCollapsed',JSON.stringify(nc));
}

export const reformatDate = (d,timeZone='UTC') => {
    let iso = new Date(d);
    // const event = moment.utc(iso).local().toLocaleString('en-US');
    let event;
    if(timeZone) {
        event = iso.toLocaleString('en-US',{timeZone: timeZone});
    } else {
        event = iso.toLocaleString('en-US');
    }

    return event;
};

export const reformatTime = (d,timeZone='UTC') => {
    let iso = new Date(d);
    let event;
    if(timeZone) {
        event = iso.toLocaleTimeString('en-US',{timeZone: timeZone,hour:'numeric',minute:'2-digit'});
    } else {
        event = iso.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'});
    }
    return event;
}

export const reformatJustDate = (d,timeZone='UTC') => {
    let iso = new Date(d);
    let event;
    if(timeZone) {
        event = iso.toLocaleDateString('en-US',{timeZone: timeZone,month:"short",day:"2-digit",year:"numeric"})
    } else {
        event = iso.toLocaleDateString('en-US',{month:"short",day:"2-digit",year:"numeric"});
    }
    return event;
}

export const timeDelta = (d1,d2) => {
    let dd1 = new Date(d1);
    let dd2 = new Date(d2);
    const secs = Math.round((dd2-dd1)/1000.);
    return secs;
}

export const isToday = (d) => {
    let today = new Date();
    // console.log(d.setHours(0,0,0),today.setHours(0,0,0))
    if(d && d instanceof Date && (d.toDateString() === today.toDateString())) {
        return true;
    } else {
        return false;
    }
}

export const pickerWallClockMs = (d) => {
    const local = ensurePickerWallClockDate(d);
    if (Number.isNaN(local.getTime())) return NaN;
    return Date.UTC(
        local.getFullYear(),
        local.getMonth(),
        local.getDate(),
        local.getHours(),
        local.getMinutes(),
        local.getSeconds(),
    );
};

/** Parse event start_date for timeline compare (camera-local wall clock, not UTC). */
export const parseEventTimelineStartMs = (startRaw) => {
    if (startRaw == null) return NaN;
    const s = String(startRaw).trim();
    const match = s.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
        const sec = match[6] != null ? Number(match[6]) : 0;
        return Date.UTC(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
            Number(match[4]),
            Number(match[5]),
            sec,
        );
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime()) && /GMT|UTC/i.test(s)) {
        return Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            d.getUTCHours(),
            d.getUTCMinutes(),
            d.getUTCSeconds(),
        );
    }
    return Number.isNaN(d.getTime()) ? NaN : d.getTime();
};

export const findAnchorHighlightEventId = (events, anchorDate) => {
    if (!events || events.length === 0 || !anchorDate) return null;
    const anchorMs = pickerWallClockMs(anchorDate);
    if (Number.isNaN(anchorMs)) return null;
    let bestId = null;
    let bestDist = Infinity;
    for (let i = 0; i < events.length; i++) {
        const ev = events[i];
        const t = parseEventTimelineStartMs(ev.startDate);
        if (Number.isNaN(t)) continue;
        const dist = Math.abs(t - anchorMs);
        if (dist < bestDist) {
            bestDist = dist;
            bestId = ev.eventId;
        }
    }
    return bestId;
};

function formatWallClockTimeFromParts(year, month, day, hour, minute) {
    const dt = new Date(Date.UTC(year, month, day, hour, minute));
    return dt.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
    });
}

function formatWallClockDateFromParts(year, month, day) {
    const dt = new Date(Date.UTC(year, month, day));
    return dt.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        timeZone: "UTC",
    });
}

/** Show event start_date clock as stored (camera local), not shifted to browser TZ. */
export const formatEventTimelineTime = (startRaw) => {
    const s = String(startRaw || "");
    const match = s.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (match) {
        return formatWallClockTimeFromParts(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
            Number(match[4]),
            Number(match[5]),
        );
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime()) && /GMT|UTC/i.test(s)) {
        return formatWallClockTimeFromParts(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            d.getUTCHours(),
            d.getUTCMinutes(),
        );
    }
    return reformatTime(startRaw, null);
};

export const formatEventTimelineDate = (startRaw) => {
    const s = String(startRaw || "");
    const match = s.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        return formatWallClockDateFromParts(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
        );
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime()) && /GMT|UTC/i.test(s)) {
        return formatWallClockDateFromParts(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
        );
    }
    return reformatJustDate(startRaw, null);
};

/** Camera-local wall-clock parts from event start_date (matches timeline display). */
export function parseEventTimelineParts(startRaw) {
    if (startRaw == null) return null;
    const s = String(startRaw).trim();
    const match = s.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
        return {
            year: Number(match[1]),
            month: Number(match[2]) - 1,
            day: Number(match[3]),
            hour: Number(match[4]),
            minute: Number(match[5]),
        };
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime()) && /GMT|UTC/i.test(s)) {
        return {
            year: d.getUTCFullYear(),
            month: d.getUTCMonth(),
            day: d.getUTCDate(),
            hour: d.getUTCHours(),
            minute: d.getUTCMinutes(),
        };
    }
    if (!Number.isNaN(d.getTime())) {
        return {
            year: d.getFullYear(),
            month: d.getMonth(),
            day: d.getDate(),
            hour: d.getHours(),
            minute: d.getMinutes(),
        };
    }
    return null;
}

function formatDayOrdinal(day) {
    const n = Number(day);
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    const suffixes = ["th", "st", "nd", "rd"];
    return `${n}${suffixes[n % 10] || "th"}`;
}

function formatHourCompact(hour24) {
    const h = hour24 % 12 || 12;
    const suffix = hour24 < 12 ? "AM" : "PM";
    return `${h}${suffix}`;
}

function sameCalendarDayParts(a, b) {
    return a.year === b.year && a.month === b.month && a.day === b.day;
}

function hourBucketKey(parts) {
    return `${parts.year}-${parts.month}-${parts.day}-${parts.hour}`;
}

function wallClockHourIndex(parts) {
    return Date.UTC(parts.year, parts.month, parts.day, parts.hour);
}

function partsMinHour(a, b) {
    return wallClockHourIndex(a) <= wallClockHourIndex(b) ? a : b;
}

function partsMaxHour(a, b) {
    return wallClockHourIndex(a) >= wallClockHourIndex(b) ? a : b;
}

/** e.g. Thursday, June 18th 3PM — or 2PM – 4PM when a range */
export function formatEventGroupHeading(startParts, endParts) {
    const dt = new Date(Date.UTC(startParts.year, startParts.month, startParts.day));
    const weekday = dt.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
    const month = dt.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    const dayOrdinal = formatDayOrdinal(startParts.day);
    const startHour = formatHourCompact(startParts.hour);

    let timeLabel = startHour;
    if (
        endParts &&
        sameCalendarDayParts(startParts, endParts) &&
        endParts.hour !== startParts.hour
    ) {
        const lo = partsMinHour(startParts, endParts);
        const hi = partsMaxHour(startParts, endParts);
        timeLabel = `${formatHourCompact(lo.hour)} – ${formatHourCompact(hi.hour)}`;
    }

    return `${weekday}, ${month} ${dayOrdinal} ${timeLabel}`;
}

const TIMELINE_MIN_GROUP_EVENTS = 3;
const TIMELINE_MAX_MERGED_HOURS = 6;

function mergeTimelineGroups(a, b) {
    const startParts = partsMinHour(a.startParts, b.startParts);
    const endParts = partsMaxHour(a.endParts, b.endParts);
    return {
        id: `${hourBucketKey(startParts)}-${hourBucketKey(endParts)}`,
        startParts,
        endParts,
        events: [...a.events, ...b.events],
    };
}

function hoursSpanned(startParts, endParts) {
    return Math.abs(wallClockHourIndex(endParts) - wallClockHourIndex(startParts)) / 3600000 + 1;
}

/**
 * Group visible motion events by hour for timeline headings.
 * Sparse hours (< 3 events) merge with an adjacent same-day group when possible.
 */
export function groupEventsForTimeline(events) {
    if (!events || events.length === 0) return [];

    const playable = events.filter((e) => e.url && e.status === "IN_S3");
    if (playable.length === 0) return [];

    const groups = [];
    let current = null;

    for (const event of playable) {
        const parts = parseEventTimelineParts(event.startDate);
        if (!parts) continue;
        const key = hourBucketKey(parts);
        if (!current || current.hourKey !== key) {
            current = {
                id: key,
                hourKey: key,
                startParts: { ...parts },
                endParts: { ...parts },
                events: [event],
            };
            groups.push(current);
        } else {
            current.events.push(event);
            current.endParts = { ...parts };
        }
    }

    let i = 0;
    while (i < groups.length) {
        const g = groups[i];
        if (g.events.length >= TIMELINE_MIN_GROUP_EVENTS || groups.length === 1) {
            i += 1;
            continue;
        }

        const next = groups[i + 1];
        const prev = groups[i - 1];

        const canMergeWithNext =
            next &&
            sameCalendarDayParts(g.startParts, next.startParts) &&
            hoursSpanned(
                partsMinHour(g.startParts, next.startParts),
                partsMaxHour(g.endParts, next.endParts),
            ) <= TIMELINE_MAX_MERGED_HOURS;

        const canMergeWithPrev =
            prev &&
            sameCalendarDayParts(g.startParts, prev.startParts) &&
            hoursSpanned(
                partsMinHour(prev.startParts, g.startParts),
                partsMaxHour(prev.endParts, g.endParts),
            ) <= TIMELINE_MAX_MERGED_HOURS;

        if (canMergeWithNext && (!canMergeWithPrev || next.events.length <= prev.events.length)) {
            groups.splice(i, 2, mergeTimelineGroups(g, next));
            continue;
        }
        if (canMergeWithPrev) {
            groups.splice(i - 1, 2, mergeTimelineGroups(prev, g));
            continue;
        }
        i += 1;
    }

    return groups.map((g) => ({
        id: g.id,
        label: formatEventGroupHeading(g.startParts, g.endParts),
        count: g.events.length,
        events: g.events,
    }));
}

export const useKeyPress = function(targetKey) {
    const [keyPressed, setKeyPressed] = useState(false);

    function downHandler({ key }) {
        if (key === targetKey) {
            setKeyPressed(true);
        }
    }

    const upHandler = ({ key }) => {
        if (key === targetKey) {
            setKeyPressed(false);
        }
    };

    useEffect(() => {
        window.addEventListener("keydown",downHandler);
        window.addEventListener("keyup",upHandler);
        return () => {
            window.removeEventListener("keydown",downHandler);
            window.removeEventListener("keyup",upHandler); 
        };
    });

    return keyPressed;
}

export const filterMonitorsByMode = (monitors,mode) => {
    var modeMonitors = [];
    for(var direction in monitors) {
        if(direction==='capId') {
            continue;
        }
        for(var quality in monitors[direction]) {
            if(monitors[direction][quality].mode === mode) {
                modeMonitors.push(monitors[direction][quality]);
            }
        }
    }
    return modeMonitors
}

export const filterMonitorsByQuality = (monitors,quality) => {
    var qualityMonitors = [];
    for(var direction in monitors) {
        if(direction==='capId') {
            continue;
        }
        qualityMonitors.push(monitors[direction][quality]);
    }
    return qualityMonitors;
}

export const readableMonitorName = (monitor) => {
    // console.log(monitor);
    if(monitor.name.startsWith('House')) return "Camera #1";
    if(monitor.name.startsWith('Neighbor')) return "Camera #2";
    if(monitor.name.startsWith('Street')) return "Camera #3";
    if(monitor.name.startsWith('Driveway')) return "Camera #4";
    return monitor.name.split('_')[0];
}

export const getMonitorIdFromModectsAndDirection = (monitors,direction)  => {
    for(var dir in monitors) {
        if(direction.toLowerCase()===dir) {
            for(var quality in monitors[dir]) {
                if(monitors[dir][quality].mode==="Modect") {
                    // console.log(monitors[dir][quality].monitorId)
                    return monitors[dir][quality].monitorId;
                }
            }
        }
    }
}

export const filterMonitorsByMonitorId = (monitors,id) => {
    for(var direction in monitors) {
        if(direction==='capId') {
            continue;
        }
        for(var quality in monitors[direction]) {
            if(monitors[direction][quality].monitorId===id) {
                return monitors[direction][quality];
            }
        }
    }
    return null;
}

export const getMonitorsGroupById = (monitors,id) => {
    for(var direction in monitors) {
        if(direction==='capId') {
            continue;
        }
        for(var quality in monitors[direction]) {
            if(monitors[direction][quality].monitorId===id) {
                return monitors[direction];
            }
        }
    }
}

export const addDaysToDate = (date,days) => {
    var d = new Date(date);
    d.setDate(d.getDate()+days);
    return d;
}

export const addHoursToDate = (date,hours) => {
    var d = new Date(date);
    d.setTime(d.getTime()+(hours*60*60*1000));
    return d;
}

export const addMinutesToDate = (date,minutes) => {
    var d = new Date(date);
    d.setTime(d.getTime()+(minutes*60*1000));
    return d;
}

/** Footage is written in 10-minute chunks; end time must be at least this far in the past. */
export const FOOTAGE_BUFFER_MINUTES = 10;

/** Latest end time allowed for a footage request (server has written chunks up to this time). Uses client "now". */
export const getLatestAllowedFootageEndTime = () =>
    new Date(Date.now() - FOOTAGE_BUFFER_MINUTES * 60 * 1000);

/**
 * Latest allowed end time for a footage request. The buffer is real time (server writes every 10 min),
 * so the cutoff is always "now minus buffer" — the same instant in all timezones. offsetMinutes is ignored.
 */
export const getLatestAllowedFootageEndTimeWithOffset = (offsetMinutes) =>
    getLatestAllowedFootageEndTime();

/**
 * Infer camera timezone offset from event.addDate (UTC) and event.startDate (camera local).
 * add_date is in UTC; start_date is usually the same moment in camera local time.
 * Returns offset in minutes: positive = camera is behind UTC (e.g. 480 = 8h = Pacific).
 * Returns null if we can't infer. Clamped to US/Canada range (~-630 to +60 minutes).
 */
export const inferEventCameraOffsetMinutes = (event) => {
    if (!event) return null;
    const addDate = event.addDate ?? event.add_date;
    if (addDate == null) return null;
    const startRaw = event.startDate;
    if (startRaw == null || typeof startRaw !== "string") return null;
    // startDate may have a Z suffix but is actually camera local; we use its clock as camera time
    const addStr = typeof addDate === "string" ? addDate.trim() : addDate;
    const addAsUtc = new Date(addStr.endsWith("Z") || addStr.endsWith("z") ? addStr : addStr + "Z");
    if (isNaN(addAsUtc.getTime())) return null;
    const addUtcMin = addAsUtc.getUTCHours() * 60 + addAsUtc.getUTCMinutes();
    const match = startRaw.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (!match) return null;
    const [, y, m, d, hr, min] = match.map(Number);
    const startMin = hr * 60 + min;
    let offsetMin = addUtcMin - startMin;
    if (offsetMin > 12 * 60) offsetMin -= 24 * 60;
    if (offsetMin < -12 * 60) offsetMin += 24 * 60;
    // return Math.max(-630, Math.min(60, offsetMin));
    return offsetMin;
};

/**
 * True if the event is too new to request footage (default end = start + 5 min must be at least 10 min in the past at camera).
 * Uses add_date (UTC) + start_date (camera local) to infer camera timezone when available.
 */
export const isEventTooNewForFootage = (event) => {
    if (!event || !event.startDate) return true;
    const offsetMinutes = inferEventCameraOffsetMinutes(event);
    const startRaw = event.startDate;
    if (typeof startRaw !== "string") return true;
    const match = startRaw.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (!match || offsetMinutes == null) {
        const eventStart = new Date(startRaw.replace(/Z?$/i, ""));
        const defaultEnd = addMinutesToDate(eventStart, 5);
        return defaultEnd > getLatestAllowedFootageEndTime();
    }
    const [, y, m, d, hr, min] = match.map(Number);
    let endMin = min + 5;
    let endHr = hr;
    if (endMin >= 60) { endMin -= 60; endHr += 1; }
    if (endHr >= 24) { endHr -= 24; }
    // Default end in camera local (endHr, endMin) → UTC timestamp (camera is offsetMinutes behind UTC)
    const defaultEndUtcMs = Date.UTC(y, m - 1, d, 0, 0, 0) + ((endHr * 60 + endMin) + offsetMinutes) * 60 * 1000;
    // Cutoff is simply "now minus buffer" — same instant in all timezones
    const latestAllowedUtcMs = Date.now() - FOOTAGE_BUFFER_MINUTES * 60 * 1000;
    return defaultEndUtcMs > latestAllowedUtcMs;
};

export const makeMonitorFilter = (monitors,selectedMonitors) => {
    let monitorIds = [];
    if(selectedMonitors) {
        var allTrue = true;
        var allFalse = true;
        for(var direction in selectedMonitors) {
            if(selectedMonitors[direction]) {
                var mId = getMonitorIdFromModectsAndDirection(monitors,direction);
                if(mId) {
                    monitorIds.push(mId);
                    allFalse = false;
                }
                var d = direction.toLowerCase();
                if(d in monitors && monitors[d]) {
                    var pId = monitors[d]['high'].monitorId;
                    if(pId) {
                        if(!(monitorIds.includes(pId))) {
                            monitorIds.push(pId);
                            allFalse = false;
                        }
                    }
                }
            } else {
                allTrue = false;
            }
        }
        if(allTrue) monitorIds = [];
        if(allFalse) monitorIds = null;
    }
    return monitorIds;
}

export const getUserInformation = () => {
    var info = {
        referrer: document.referrer,
        previousSites: history.length,
        browser: navigator.appName,
        browserEngine: navigator.product,
        browserVersion: navigator.appVersion,
        browserAgent: navigator.userAgent,
        browserLanguage: navigator.language,
        browserOnline: navigator.onLine,
        browserPlatform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled,
        cookies: document.cookie,
        cookiesDecoded: decodeURIComponent(document.cookie.split(';')),
        storage: localStorage,
        sizeScreenW: screen.width,
        sizeScreenH: screen.height,
        sizeDocW: document.width,
        sizeDocH: document.height,
        sizeInW: innerWidth,
        sizeInH: innerHeight,
        sizeAvailW: screen.availWidth,
        sizeAvailH: screen.availHeight,
        scrColorDepth: screen.colorDepth,
        scrPixelDepth: screen.pixelDepth,
        // latitude: position.coords.latitude,
        // longitude: position.coords.longitude,
        // accuracy: position.coords.accuracy,
        // altitude: position.coords.altitude,
        // altitudeAccuracy: position.coords.altitudeAccuracy,
        // heading: position.coords.heading,
        // speed: position.coords.speed,
        // timestamp: position.timestamp,
    }
    return info;
}

export const handleNameChange = (e) => {
    const name = e.target.value.replace(/(<([^>]+)>)/ig,"");
    eventName.current = name;
    // putEventName(event.eventId, name)
};

export const pasteAsPlainText = (e) => {
    e.preventDefault();

    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertHTML',false,text);
};

export const disableNewLines = (e) => {
    const keyCode = e.keyCode || e.which;

    if(keyCode === 13) {
        e.returnValue = false;
        if (e.preventDefault) e.preventDefault();
        const element = (e.currentTarget || e.srcElement);
        element.blur();
    }
};

export const highlightAll = (e) => {
    // // e.target.select()
    e.preventDefault();
    document.execCommand('selectAll',false,null);
    // var range = document.createRange();
    // range.selectNodeContents(e.current.getEl());
    // var sel = window.getSelection();
    // sel.removeAllRanges();
    // sel.addRange(range);
}

export const renderAccessDenied = () => {
    return (
        <div style={{paddingTop:"100px",textAlign: "center"}}>
            <h3>You do not have access to this page, please check with your account owner</h3>
        </div>
    )
}

export const mobileCheck = () => {
    let check = false;
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
    return check;
}

export const arraysEqual = (a,b) => {
    if(a===b) return true;
    if(a == null || b == null) return false;
    if(a.length !== b.length) return false;
    for(var i=0; i<a.length; i++) {
        if(a[i]!==b[i]) return false;
    }
    return true;
}

export const pwaInstalled = () => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    } else {
        return false;
    }
}

export const iosDevice = () => {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',   
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
};

export const jumpToInstructions = (isUserAgent,linkId,instruction,extra="") => {
    if(isUserAgent) return (
        <div style={{paddingBottom:"10px"}}>
            <Button
                className="btn-brand-dark"
                bsSize="large"
                href={instruction+"#"+linkId+extra}
                block
            >
                Jump to {linkId.charAt(0).toUpperCase()+linkId.slice(1)} Instructions
            </Button>
        </div>
    );
    else return (<div></div>);
};

export const firefoxBrowser = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
}

export const notificationsAreSupported = () => {
    return 'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window
}

export const shuffle = (list) => {
    let current = list.length;

    while(current != 0) {
        let rando = Math.floor(Math.random()*current);
        current--;

        [list[current], list[rando]] = [list[rando],list[current]]
    }

    return list;
}

export const chunk = (input, size) => {
    if(input==null) return [];
    return input.reduce((arr,item,idx) => {
        return idx % size === 0 ? [...arr, [item]] : [...arr.slice(0,-1), [...arr.slice(-1)[0], item]];
    }, []);
};

export const toProperCase = (str) => {
    if(!str) return '';
    return str.toLowerCase().split(' ').map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
}

export const formatPhoneNumber = (phoneNumberString) => {
  if (!phoneNumberString) return null;
  
  // Remove all non-digit characters
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  
  // Handle international format (+1XXXXXXXXXX)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
  }
  
  // Handle standard 10-digit format
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  
  // Return original if no match
  return phoneNumberString;
};

export const numberToWords = (num, capitalize=false) => {
  if(num > 10) return num.toString();
  const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
  if(capitalize) return words[num].charAt(0).toUpperCase() + words[num].slice(1);
  else return words[num];
}

// Check if user has OWNER access to any caps
export const hasOwnerAccess = (userCaps) => {
  if (!userCaps || !Array.isArray(userCaps)) return false;
  return userCaps.some(userCap => userCap.accessType === 'OWNER');
}

export const reparseCaps = (caps) => {
  return caps.map(cap => ({
    ...cap,
    capId: cap.capid,
    builderId: cap.builderid,
    defaultMonitorId: cap.defaultmonitorid,
    neighborhoodId: cap.neighborhoodid,
  }));
}